import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Groups2Icon from '@mui/icons-material/Groups2';
import PaymentsIcon from '@mui/icons-material/Payments';
import { Alert, Box, Divider, Paper, TextField, Typography } from '@mui/material';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { expertiseService } from 'features/cost-analysis/team/expertises/api/expertise.service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from 'shared/types/general';
import { IRoleName } from '../../role-names';
import { staffRoleService } from '../../role-names/api/staff-role.service';
import { IExpertise } from '../types';

interface CategoryFormProps {
  expertise: IExpertise;
  onSubmit: (values) => any;
}
export const ExpertiseForm = ({ expertise, onSubmit }: CategoryFormProps) => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Option[]>([]);
  const [roleNames, setRoleNames] = useState<IRoleName[]>([]);
  const [roleName, setRoleName] = useState<IRoleName>();
  const [selectedFte, setSelectedFte] = useState(expertise?.fte);

  useEffect(() => {
    staffRoleService.getAllPaginated().then((res) => {
      setRoles(res.results);
    });
    expertiseService.getRoleNames().then((res) => setRoleNames(res.results));
  }, []);

  useEffect(() => {
    if (expertise) setSelectedFte(expertise?.fte);
  }, [expertise]);

  const _submit = (values) => {
    const _values = {
      ...values,
      fte: selectedFte
    };
    onSubmit(_values);
  };

  return (
    <div className="mt-6">
      {expertise && (
        <Paper variant="outlined" className="p-6 rounded-xl border-slate-200 shadow-sm bg-white">
          <EnhancedForm onSubmit={_submit} edit={Boolean(expertise.id)}>
            <div className="grid grid-cols-12 gap-8 items-start">
              {/* Left Column: Icon View */}

              <div className="col-span-2 flex flex-col items-center justify-center p-4 bg-slate-50 rounded-lg min-h-[160px]">
                <Groups2Icon color="primary" sx={{ fontSize: 72 }} className="drop-shadow-sm" />
              </div>

              {/* Middle Column: Main Inputs */}
              <div className="col-span-5 flex flex-col gap-5 pr-4 border-r border-slate-200">
                {roles && (
                  <IdentityField
                    name="role"
                    component={AutocompleteAdapter}
                    initValue={roles.find((s) => s.code == expertise.role)}
                    options={roles}
                    label={t('entities.expertise.role')}
                    type="text"
                  />
                )}

                <IdentityField
                  name="role_name"
                  component={AutocompleteAdapter}
                  options={roleNames}
                  setValue={setRoleName}
                  optionValue={'code'}
                  optionLabel={'code'}
                  initValue={roleNames.find((s) => s.code == expertise.role_name)}
                  label={t('entities.expertise.role_name')}
                  type="text"
                />

                <IdentityField
                  name="name"
                  component={TextFieldAdapter}
                  initialValue={expertise.name}
                  label={t('entities.expertise.name')}
                  type="text"
                />
              </div>

              {/* Right Column: Secondary Inputs */}
              <div className="col-span-5 flex flex-col gap-5 pl-4">
                {roleName && expertise && (
                  <div className="w-full mb-4">
                    <TextField
                      name={'fte'}
                      value={selectedFte}
                      type="number"
                      label={`${t('entities.expertise.fte')} (max: ${
                        roleName ? +(roleName.fte - expertise.role_name_fte).toFixed(2) : '---'
                      })`}
                      onChange={(e) => {
                        setSelectedFte(+e.target.value);
                      }}
                      fullWidth
                    />
                  </div>
                )}
                {roleName && selectedFte && (
                  <div className="w-full mb-4">
                    {roleName.fte - (roleName.counters?.fte + selectedFte - expertise.fte) < 0 && (
                      <Alert severity="error">{t('pages.expertises.new.fte-exceeded')}</Alert>
                    )}

                    <Box className="p-4 bg-slate-50 border border-slate-100 rounded-xl flex flex-col gap-3 mt-auto">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-slate-600">
                          <PaymentsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <span className="text-sm">{t('entities.expertise.monthly_cost')}</span>
                        </div>
                        <Typography variant="body2" className="font-bold text-slate-800">
                          {currencyFormatter(expertise.monthly_cost)}
                        </Typography>
                      </div>

                      <Divider className="border-slate-100" />

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-slate-600">
                          <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <span className="text-sm">{t('entities.expertise.monthly_hours')}</span>
                        </div>
                        <Typography variant="body2" className="font-bold text-slate-800">
                          {(+expertise.monthly_hours).toFixed(2).replace('.', ',')} ore
                        </Typography>
                      </div>
                    </Box>
                  </div>
                )}
              </div>
            </div>
          </EnhancedForm>
        </Paper>
      )}
    </div>
  );
};

export default ExpertiseForm;
